/* eslint-disable import/prefer-default-export */
import { createAsyncThunk, AsyncThunkPayloadCreator } from '@reduxjs/toolkit'
import PQueue from 'p-queue'
import { Page } from 'features/ui/types'
import * as liveReportServiceAPI from 'liveReportService'
import * as API from '../../api'
import { convertResult } from '../../utils/runSearchUtil'
import { ServiceTypeStrings } from '../../enums/ServiceType'
import { RunSearch } from '../../schemas/action/RunSearchSchema'
import { DashboardWidgetData } from './types'

const SEARCH_QUEUE_CAPACITY = 10
const queue = new PQueue({ concurrency: SEARCH_QUEUE_CAPACITY })

export interface GetWidgetForDashboardParam {
  searches: RunSearch[]
  widgetId: number
  searchesIdx?: Record<string, number>
  timestamp?: number
  url?: string
  // Per-dispatch opt-in to route the request through the LLM Word Cloud
  // pipeline (monitor-app PR #62). Forwarded to dynamicAPI.runSearch and
  // converted to a `llm_wordcloud=true` form field there.
  llmWordCloud?: boolean
}

const getWidgetData: AsyncThunkPayloadCreator<
  { results: DashboardWidgetData[]; resultsIdx?: Record<string, number>; url?: string },
  GetWidgetForDashboardParam
> = async (
  { searches, widgetId, searchesIdx, timestamp, url, llmWordCloud },
  { rejectWithValue, getState }
) => {
  try {
    const promises: Promise<any>[] = []
    const serivceTypes: ServiceTypeStrings[] = []
    const namesMaps: Map<string, string>[] = []

    // handle dynamic API
    const {
      ui: { page }
    } = getState() as { ui: { page: string } }
    const dynamicAPI = page === Page.LiveReport ? liveReportServiceAPI : API

    // TODO: no dashboardId, dashboardName
    // @ts-ignore
    searches.forEach(({ queryData, dashboardId, dashboardName, service, namesMap }, idx) => {
      const spec = queryData!.keyMap!.get('spec')
      const response = queue.add(() =>
        dynamicAPI.runSearch(
          dashboardId,
          dashboardName,
          spec,
          widgetId,
          timestamp,
          undefined,
          llmWordCloud
        )
      )
      promises.push(response)
      serivceTypes[idx] = service.type!
      namesMaps[idx] = namesMap
    })
    const results = (await Promise.all(promises)).map((res, idx) => ({
      ...convertResult(serivceTypes[idx], namesMaps[idx], res.data),
      widgetId
    }))
    return { results, resultsIdx: searchesIdx, url }
  } catch (e: unknown) {
    console.error(e)
    return rejectWithValue((e as API.FetchError)?.response?.data)
  }
}

export const getWidgetForDashboard = createAsyncThunk('widget/getWidgetForDashboard', getWidgetData)

export const getMoreWidgetForDashboard = createAsyncThunk(
  'widget/getMoreWidgetForDashboard',
  getWidgetData
)

export const getPrerequisiteWidgetForDashboard = createAsyncThunk(
  'widget/getPrerequisiteWidgetForDashboard',
  getWidgetData
)

export interface GetWidgetForAnalyzeParam {
  spec: string
}
export const getWidgetForAnalyze = createAsyncThunk(
  'widget/getWidgetForAnalyze',
  async ({ spec }: GetWidgetForAnalyzeParam) => {
    const response = await API.runSearch(-1, '', spec)
    // widgetId is responsible for connecting widget and it's runSearch result in the front-end
    return response.data
  }
)

interface GetDataForCustomVizWidget {
  widgetId: number
}
export const getDataForCustomVizWidget = createAsyncThunk(
  'widget/getDataForCustomVizWidget',
  async ({ widgetId }: GetDataForCustomVizWidget, { rejectWithValue }) => {
    try {
      const response = await API.getCustomVizWidgetData(widgetId)

      return { data: response.data, widgetId }
    } catch (e: any) {
      console.error(e)
      return rejectWithValue(e.response.data)
    }
  }
)

export const getWidgetPostSoundbites = createAsyncThunk(
  'widget/getWidgetPostSoundbites',
  getWidgetData
)
